/**
 * Fase 2 «Arquitetura de Conduta e Contexto» §C.6 — a CLASSE do vínculo.
 *
 * ── O problema que isto resolve ───────────────────────────────────────────────────────
 * O motor precisa dizer ao modelo **quem esta pessoa é para nós** — o nível 6 dos 6
 * níveis. O que ele tem é `Bond.type`, um dos 21 tipos canônicos da ADR-037/131. Injetar
 * o tipo cru seria errado por dois motivos: (a) 21 valores é ruído, e o modelo não tem o
 * que fazer com a diferença entre `home_care` e `social_care_case` na hora de escolher o
 * tom; (b) o rótulo específico do vínculo é **dado do tenant** («seu plano», «seu
 * processo», «sua matrícula») e não deve nascer em código — o erro histórico que custou
 * 28 sessões (regra 6 de `.claude/rules/motor-ia-conductor.md`).
 *
 * O que É de plataforma (camada 1) é a **classe**: classificar em «serviço continuado ×
 * caso episódico × bem específico × relação em formação» vale para uma clínica, um
 * escritório e uma loja ao mesmo tempo — que é o teste da camada 1. Nomear o vínculo
 * continua sendo do tenant.
 *
 * ── A garantia estrutural ─────────────────────────────────────────────────────────────
 * `BOND_TYPE_FAMILY` é um `Record<BondType, BondFamily>` **exaustivo por tipo**: um tipo
 * novo de Bond sem família declarada é **erro de compilação**, não um caso que cai no
 * `else` em silêncio (memória `reference_detector_lista_de_vocabulario_vaza` — lista de
 * vocabulário em detector vaza para sempre; aqui a lista é o próprio tipo).
 *
 * ⚠️ **DECISÃO DO TL pendente** (é taxonomia de PRODUTO, não mecânica): o agrupamento
 * abaixo é a **recomendação** do desenho §C.6, não uma ratificação. A alternativa
 * considerada era 3 famílias, fundindo `asset` em `continuous_service` — ao custo de
 * perder a única linha que manda a IA identificar DE QUAL bem se trata antes de agir.
 * O delta de uma re-taxonomia mora em `bond-families.spec.ts`.
 *
 * ⚠️ **Nada de PROSA aqui.** Este módulo entrega a CLASSE; a frase que a IA lê é
 * responsabilidade do renderizador do quadro do atendimento, no engine (PR posterior).
 * Manter a taxonomia separada da prosa é o que permite ao guard de neutralidade auditar
 * um texto fixo em um só lugar.
 */
export const BOND_FAMILIES = [
    /** Relação em curso com a empresa — o cliente «é da casa». */
    'continuous_service',
    /** Um caso/processo com começo e fim, cujo ANDAMENTO é o assunto provável. */
    'episodic_case',
    /** O vínculo é com um BEM específico — qual deles é a primeira pergunta. */
    'asset',
    /** Relação ainda em formação (interesse/negociação) — ainda não é cliente. */
    'prospect',
];
/**
 * Os 21 tipos canônicos → 4 classes. Exaustivo por construção (`Record<BondType, …>`).
 *
 * Racional de cada família, para que uma revisão futura discuta o critério e não os itens:
 *  - `continuous_service`: existe uma relação VIVA e recorrente; o cliente não é novo e
 *    conduzir «do zero» é o erro. Inclui cuidado/ensino continuados (`patient_care`,
 *    `student`, `home_care`, `social_care_case`), relações comerciais duradouras
 *    (`vendor_relationship`, `partnership`) e trabalho em curso (`project`).
 *  - `episodic_case`: há um OBJETO de tramitação com estado próprio; perguntar pelo caso
 *    antes de abrir frente nova é a conduta certa. `procurement` entra aqui (um processo
 *    de compra tramita), e não em `prospect`, porque já existe processo instaurado.
 *  - `asset`: a ambiguidade DE QUAL bem é a falha de maior consequência.
 *  - `prospect`: cobrar dados de quem ainda não é cliente é o erro característico.
 */
export const BOND_TYPE_FAMILY = {
    contract: 'continuous_service',
    'customer-account': 'continuous_service',
    'rental-contract': 'continuous_service',
    'patient-care': 'continuous_service',
    student: 'continuous_service',
    'home-care': 'continuous_service',
    'social-care-case': 'continuous_service',
    'vendor-relationship': 'continuous_service',
    partnership: 'continuous_service',
    project: 'continuous_service',
    'legal-case': 'episodic_case',
    mediation: 'episodic_case',
    'compliance-case': 'episodic_case',
    procurement: 'episodic_case',
    property: 'asset',
    vehicle: 'asset',
    equipment: 'asset',
    opportunity: 'prospect',
    interest: 'prospect',
    'purchase-intent': 'prospect',
    'transaction-attempt': 'prospect',
};
/** Classe de um tipo de vínculo do DOMÍNIO (kebab-case). Total por construção. */
export function bondTypeToFamily(type) {
    return BOND_TYPE_FAMILY[type];
}
/**
 * Índice tolerante, aceitando a forma do DOMÍNIO (kebab) **e** a do banco (snake).
 *
 * Por que existe: o consumidor do motor recebe o `Bond.type` pela projeção do
 * `ConfigCacheService`, e ali o valor é o do enum **Prisma** (`customer_account`),
 * enquanto `@lume/shared` é canônico em kebab (`customer-account`) — a fronteira é
 * traduzida em `bonds.mapping.ts`, que vive no engine e não pode ser importado daqui.
 * Sem esta função cada caller re-inventaria a normalização, que é exatamente a classe
 * «dois caminhos para a mesma ideia» (memória `feedback_quantos_caminhos_chegam_aqui`).
 */
const FAMILY_BY_NORMALIZED = new Map(Object.entries(BOND_TYPE_FAMILY).flatMap(([type, family]) => [
    [type, family],
    [type.replace(/-/g, '_'), family],
]));
/**
 * Classe de um valor CRU de tipo de vínculo. `null` para ausente/desconhecido — nunca
 * lança e nunca chuta: o caminho quente do conductor não pode cair porque um valor novo
 * de enum chegou do banco antes do deploy do código, e uma classe errada seria pior que
 * classe nenhuma (a linha do quadro degrada para «ainda sem vínculo conhecido»).
 */
export function resolveBondFamily(raw) {
    if (typeof raw !== 'string')
        return null;
    const key = raw.trim().toLowerCase();
    if (key.length === 0)
        return null;
    return FAMILY_BY_NORMALIZED.get(key) ?? null;
}
//# sourceMappingURL=bond-families.js.map